// React imports
import React, { useState, useEffect } from 'react';
// Material UI components
import {
	Box,
	Typography,
	Grid,
	Card,
	CardContent,
	LinearProgress,
	Avatar,
	Paper,
	Select,
	MenuItem,
	FormControl,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useComponentStyles } from '../../../../hooks/useComponentStyles';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
// Recharts components
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	ComposedChart,
	Area,
	Line,
} from 'recharts';
// Material UI icons
import {
	CalendarTodayOutlined as CalendarIcon,
	KeyboardArrowDown as ArrowDownIcon,
	TrendingUpOutlined as TrendingUpIcon,
} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import MedicationIcon from '@mui/icons-material/Medication';
// Local assets
import coinIcon from '../images/coin.png';
import revenueIcon from '../images/revenue.png';
import profitIcon from '../images/profit.png';
import costIcon from '../images/cost.png';
import cancelIcon from '../images/cancel.png';
import purchaseIcon from '../images/purchase.png';
import categoriesIcon from '../images/categories.png';
import quantityIcon from '../images/quantity.png';

import RestClient from '../../../../api/axiosInterceptor';

const Dashboard = () => {
	const theme = useTheme();
	const styles = useComponentStyles();
	const [timePeriod, setTimePeriod] = useState('monthly');
	const [drugs, setDrugs] = useState([]);
	const [orders, setOrders] = useState([]);
	const [topProducts, setTopProducts] = useState([]);
	const [totalUnitsSold, setTotalUnitsSold] = useState(0);
	const [totalProfit, setTotalProfit] = useState(0);
	const [totalSales, setTotalSales] = useState(0);
	const [paidOrdersValue, setPaidOrdersValue] = useState(0);
	const [paidOrdersCount, setPaidOrdersCount] = useState(0);
	const [totalCost, setTotalCost] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const handleTimePeriodChange = (event) => {
		setTimePeriod(event.target.value);
	};

	// Fetch drugs on component mount
	useEffect(() => {
		fetchDrugs();
		const handleDrugDeleted = () => {
			console.log('Drug deletion detected, refreshing drugs list');
			fetchDrugs();
		};
		const handleDrugUpdated = () => {
			console.log('Drug added/updated, refreshing drugs list');
			fetchDrugs();
		};
		window.addEventListener('drugDeleted', handleDrugDeleted);
		window.addEventListener('drugUpdated', handleDrugUpdated);

		return () => {
			window.removeEventListener('drugDeleted', handleDrugDeleted);
			window.removeEventListener('drugUpdated', handleDrugUpdated);
		};
	}, []);

	// fetchDrugs function with filtering for deleted items
	const fetchDrugs = async () => {
		try {
			console.log('Fetching drugs data...');
			setLoading(true);

			const response = await fetch('/medication/drug/', {
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate',
					Pragma: 'no-cache',
					Expires: '0',
				},
			});

			if (!response.ok) {
				throw new Error('Failed to fetch drugs');
			}

			const data = await response.json();
			console.log(`Fetched ${data.length} drugs from server`);

			const activeDrugs = data.filter((drug) => !drug.is_deleted);
			console.log(`Filtered to ${activeDrugs.length} active drugs`);

			setDrugs(activeDrugs);
			setLoading(false);
		} catch (err) {
			console.error('Error fetching drugs:', err);
			setLoading(false);
		}
	};

	// Fetch Categories
	const getUniqueCategories = () => {
		const uniqueCategories = new Set();

		drugs.forEach((drug) => {
			if (drug.categories && drug.categories.length > 0) {
				drug.categories.forEach((category) => {
					uniqueCategories.add(category.name);
				});
			}
		});

		return uniqueCategories.size;
	};

	const getTotalQuantity = () => {
		return drugs
			.reduce((total, drug) => {
				return total + (drug.stock || 0);
			}, 0)
			.toLocaleString();
	};

	// Low stock items
	const getLowStockItems = () => {
		return drugs
			.filter((drug) => !drug.is_deleted && drug.stock <= drug.reorder_level)
			.sort((a, b) => a.stock - b.stock);
	};

	// Monthly chart data
	const [monthlyChartData, setMonthlyChartData] = useState([
		{ month: 'Jan', sales: 0, purchase: 0 },
		{ month: 'Feb', sales: 0, purchase: 0 },
		{ month: 'Mar', sales: 0, purchase: 0 },
		{ month: 'Apr', sales: 0, purchase: 0 },
		{ month: 'May', sales: 0, purchase: 0 },
		{ month: 'Jun', sales: 0, purchase: 0 },
		{ month: 'Jul', sales: 0, purchase: 0 },
		{ month: 'Aug', sales: 0, purchase: 0 },
		{ month: 'Sep', sales: 0, purchase: 0 },
		{ month: 'Oct', sales: 0, purchase: 0 },
		{ month: 'Nov', sales: 0, purchase: 0 },
		{ month: 'Dec', sales: 0, purchase: 0 },
	]);

	// Fetch orders
	useEffect(() => {
		const fetchOrders = async () => {
			try {
				// Use the already fetched drugs
				const drugsMap = {};
				drugs.forEach((drug) => {
					drugsMap[drug.id] = parseFloat(drug.cost_price || 0);
				});

				const response = await fetch('/medication/orders/');
				if (!response.ok) throw new Error('Failed to fetch orders');
				const data = await response.json();

				const filteredOrders = data.filter((order) => {
					const totalQuantity =
						order.items?.reduce(
							(total, item) => total + (item.quantity || 0),
							0,
						) || 0;
					return totalQuantity > 0;
				});

				setOrders(filteredOrders);

				const validPaymentMethods = [
					'mpesa',
					'visa',
					'mastercard',
					'paypal',
					'stripe',
				];
				const paidOrders = filteredOrders.filter(
					(order) =>
						order.payment_method &&
						validPaymentMethods.includes(order.payment_method.toLowerCase()),
				);

				// Calculate REVENUE (total monetary value)
				const paidOrdersTotal = paidOrders.reduce(
					(sum, order) => sum + Number(order.total_price || 0),
					0,
				);
				setPaidOrdersValue(paidOrdersTotal);

				// Calculate SALES (total quantity sold)
				const totalUnitsSold = paidOrders.reduce((sum, order) => {
					if (order.items && Array.isArray(order.items)) {
						return (
							sum +
							order.items.reduce(
								(itemSum, item) => itemSum + (item.quantity || 0),
								0,
							)
						);
					}
					return sum;
				}, 0);
				setTotalUnitsSold(totalUnitsSold);

				setPaidOrdersCount(paidOrders.length);

				// Calculate COST
				let totalProductCost = 0;

				// NEW: Map for tracking top selling products
				const productMap = new Map();

				paidOrders.forEach((order) => {
					if (order.items && Array.isArray(order.items)) {
						order.items.forEach((item) => {
							if (item.drug && item.drug.id) {
								const costPrice = parseFloat(item.drug.cost_price || 0);
								const quantity = Number(item.quantity || 0);
								const price = Number(item.price_per_unit || item.price || 0);

								if (costPrice > 0) {
									const itemCost = costPrice * (item.quantity || 1);
									totalProductCost += itemCost;
								}

								// NEW: Track product data for Top Selling Stock
								const productName =
									item.drug.name || item.drug.drug_name || 'Unknown Product';

								// Get category
								let categoryName = 'Uncategorized';
								if (
									item.drug.categories &&
									Array.isArray(item.drug.categories) &&
									item.drug.categories.length > 0
								) {
									categoryName = item.drug.categories[0].name;
								}

								// Add or update product in map
								const currentProduct = productMap.get(productName) || {
									name: productName,
									category: categoryName,
									stock: item.drug.stock || 0,
									turnover: 0,
									sold: 0,
								};

								currentProduct.turnover += price * quantity;
								currentProduct.sold += quantity;
								productMap.set(productName, currentProduct);
							}
						});
					}
				});

				setTotalCost(totalProductCost);

				// Calculate PROFIT
				const totalProfit = paidOrdersTotal - totalProductCost;
				setTotalProfit(totalProfit);

				// Monthly data calculation:
				const monthlyData = Array(12)
					.fill()
					.map(() => ({
						revenue: 0,
						cost: 0,
					}));

				paidOrders.forEach((order) => {
					const orderDate = new Date(order.created_at);
					const monthIndex = orderDate.getMonth();

					monthlyData[monthIndex].revenue += Number(order.total_price || 0);

					// Calculate cost for this order and add to monthly cost
					if (order.items && Array.isArray(order.items)) {
						let orderCost = 0;
						order.items.forEach((item) => {
							if (item.drug && item.drug.id) {
								const costPrice = parseFloat(item.drug.cost_price || 0);
								if (costPrice > 0) {
									orderCost += costPrice * (item.quantity || 1);
								}
							}
						});
						monthlyData[monthIndex].cost += orderCost;
					}
				});

				// Format for chart component
				const months = [
					'Jan',
					'Feb',
					'Mar',
					'Apr',
					'May',
					'Jun',
					'Jul',
					'Aug',
					'Sep',
					'Oct',
					'Nov',
					'Dec',
				];

				const formattedChartData = monthlyData.map((data, index) => ({
					month: months[index],
					revenue: data.revenue,
					cost: data.cost,
				}));

				setMonthlyChartData(formattedChartData);

				// NEW: Process and set top products
				const sortedProducts = Array.from(productMap.entries())
					.sort((a, b) => b[1].sold - a[1].sold)
					.slice(0, 5)
					.map(([name, data]) => ({
						name,
						category: data.category,
						stock: data.stock,
						sold: data.sold,
						turnover: data.turnover,
						price: (data.turnover / data.sold).toFixed(2),
					}));

				console.log('Top selling products:', sortedProducts);
				setTopProducts(sortedProducts);
			} catch (error) {
				console.error('Error fetching orders:', error);
				setError('Failed to load orders');
			} finally {
				setLoading(false);
			}
		};

		fetchOrders();
	}, [drugs]);

	const getMonthlyOrderStats = (ordersData, monthsToShow = 6) => {
		// Validate monthsToShow parameter
		const validMonthsToShow = monthsToShow === 12 ? 12 : 6;

		if (!ordersData || ordersData.length === 0) {
			// Return empty data starting from January
			const emptyMonths = [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			].slice(0, validMonthsToShow);

			return emptyMonths.map((month) => ({
				month,
				orders: 0,
				delivered: 0,
			}));
		}

		// Initialize monthly data for all 12 months
		const months = [
			'Jan',
			'Feb',
			'Mar',
			'Apr',
			'May',
			'Jun',
			'Jul',
			'Aug',
			'Sep',
			'Oct',
			'Nov',
			'Dec',
		];
		const monthlyStats = months.map((month) => ({
			month,
			orders: 0,
			delivered: 0,
		}));

		// Process each order
		ordersData.forEach((order, index) => {
			const dateField = findDateField(order);

			if (dateField) {
				try {
					const orderDate = new Date(dateField);
					if (!isNaN(orderDate.getTime())) {
						const monthIndex = orderDate.getMonth();
						monthlyStats[monthIndex].orders++;

						// Check if order status indicates delivery
						const status = (order.status || '').toLowerCase();
						if (
							['delivered', 'completed', 'done', 'shipped'].includes(status)
						) {
							monthlyStats[monthIndex].delivered++;
						}
					}
				} catch (error) {
					// Silent error handling
				}
			} else {
				// Fallback distribution for orders without dates
				const monthIndex = index % validMonthsToShow;
				monthlyStats[monthIndex].orders++;
				if (index % 5 !== 0) monthlyStats[monthIndex].delivered++;
			}
		});

		// Return the requested number of months starting from January
		return monthlyStats.slice(0, validMonthsToShow);
	};

	// Helper to find date field in order object
	const findDateField = (obj) => {
		if (!obj) return null;

		// Direct field check
		if (obj.created_at) return obj.created_at;
		if (obj.date_created) return obj.date_created;
		if (obj.created) return obj.created;
		if (obj.date) return obj.date;

		// Search in nested objects one level deep
		for (const key in obj) {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				if (obj[key].created_at) return obj[key].created_at;
				if (obj[key].date_created) return obj[key].date_created;
				if (obj[key].created) return obj[key].created;
				if (obj[key].date) return obj[key].date;
			}
		}

		return null;
	};

	// Helper function to get status styling
	const getStatusStyle = (status) => {
		switch (status) {
			case 'Confirmed':
				return { bgcolor: '#DCFCE7', color: '#166534' };
			case 'Pending':
				return { bgcolor: '#FEF9C3', color: '#854D0E' };
			case 'Shipped':
				return { bgcolor: '#E0F2FE', color: '#0369A1' };
			case 'Canceled':
				return { bgcolor: '#FEE2E2', color: '#B91C1C' };
			default:
				return { bgcolor: '#F3F4F6', color: '#4B5563' };
		}
	};

	// Styles for the statistic boxes
	const statBoxStyles = {
		box: {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			borderRight: '1px solid #E5E7EB',
		},
		icon: {
			width: '22px',
			height: '22px',
			marginBottom: '4px',
			objectFit: 'contain',
		},
		dataContainer: {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'space-evenly',
			width: '100%',
			paddingX: '12px',
		},
		value: {
			fontSize: '0.8rem',
			fontWeight: 'bold',
			color: '#5D6679',
		},
		label: {
			fontSize: '0.65rem',
		},
	};

	// StatBox component
	const StatBox = ({ icon, value, label, hideBorder = false }) => (
		<Box
			sx={{
				...statBoxStyles.box,
				...(hideBorder
					? { borderRight: 'none' }
					: {
							borderRight:
								theme.palette.mode === 'dark'
									? '1px solid rgba(255, 255, 255, 0.09)'
									: '1px solid rgba(0, 0, 0, 0.06)',
						}),
			}}
		>
			<img src={icon} alt={label} style={statBoxStyles.icon} />
			<Box sx={statBoxStyles.dataContainer}>
				<Typography variant="h6" sx={statBoxStyles.value}>
					{value}
				</Typography>
				<Typography variant="body2" sx={statBoxStyles.label}>
					{label}
				</Typography>
			</Box>
		</Box>
	);

	return (
		<Box
			sx={{
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				overflowY: { xs: 'auto', md: 'hidden' },
				minHeight: { xs: '100vh', md: 'auto' },
				pt: 0,
				pb: { xs: 4, md: 0 },
				boxSizing: 'border-box',
			}}
		>
			<Grid
				container
				spacing={{ xs: 0.5, md: 1.5 }}
				sx={{
					flexGrow: 1,
					height: { xs: 'auto', md: 'calc(100% - 40px)' },
					position: 'relative',
					mt: 0,
				}}
			>
				{/* First Column */}
				<Grid
					item
					xs={12}
					md={7}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: { xs: 'auto', md: '100%' },
					}}
				>
					{/* Sales Overview Card */}
					<Card
						sx={{
							...styles.card,
							mb: { xs: 1, md: 1 },
							flex: { xs: '0 0 auto', md: '0 0 15%' },
							width: '100%',
						}}
					>
						<CardContent sx={{ ...styles.cardContent, p: { xs: 2, md: 2 } }}>
							<Typography
								variant="subtitle2"
								sx={{
									...styles.title,
									mb: 1,
								}}
							>
								Sales Overview
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexWrap: { xs: 'wrap', sm: 'nowrap' },
									height: { xs: 'auto', md: '60px' },
								}}
							>
								<StatBox
									icon={coinIcon}
									value={totalUnitsSold}
									label="Sales"
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
										mb: { xs: 1, sm: 0 },
									}}
								/>
								{/* Revenue */}
								<StatBox
									icon={coinIcon}
									value={`KES ${paidOrdersValue.toFixed(2)}`}
									label="Revenue"
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
										mb: { xs: 1, sm: 0 },
									}}
								/>
								<StatBox
									icon={profitIcon}
									value={`KES ${totalProfit.toFixed(2)}`}
									label="Profit"
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
										mb: { xs: 1, sm: 0 },
									}}
								/>
								<StatBox
									icon={costIcon}
									value={`KES ${totalCost.toFixed(2)}`}
									label="Cost"
									hideBorder={true}
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
									}}
								/>
							</Box>
						</CardContent>
					</Card>

					{/* Purchase Overview Card */}
					<Card
						sx={{
							...styles.card,
							mb: { xs: 1, md: 1 },
							flex: { xs: '0 0 auto', md: '0 0 15%' },
							width: '100%',
						}}
					>
						<CardContent
							sx={{
								...styles.cardContent,
								p: { xs: 2, md: 2 },
							}}
						>
							<Typography
								variant="subtitle2"
								sx={{
									...styles.title,
									mb: 1,
								}}
							>
								Purchase Overview
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexWrap: { xs: 'wrap', sm: 'nowrap' },
									height: { xs: 'auto', md: '60px' },
								}}
							>
								<StatBox
									icon={purchaseIcon}
									value={orders.length}
									label="Total Orders"
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
										mb: { xs: 1, sm: 0 },
									}}
								/>
								<StatBox
									icon={coinIcon}
									value={`KES ${orders
										.reduce(
											(sum, order) =>
												sum + (parseFloat(order.total_price) || 0),
											0,
										)
										.toFixed(2)}`}
									label="Cost"
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '50%', sm: 'auto' },
										mb: { xs: 1, sm: 0 },
									}}
								/>
								<StatBox
									icon={cancelIcon}
									value={
										orders.filter((order) => order.status === 'Cancelled')
											.length
									}
									label="Cancels"
									hideBorder={true}
									darkMode={theme.palette.mode === 'dark'}
									sx={{
										...styles.statBox,
										width: { xs: '100%', sm: 'auto' },
									}}
								/>
							</Box>
						</CardContent>
					</Card>

					{/* Sales & Purchase Chart Card */}
					<Card
						sx={{
							...styles.card,
							mb: { xs: 1, md: 1 },
							flex: { xs: '0 0 auto', md: '1 0 35%' },
							width: '100%',
						}}
					>
						<CardContent sx={{ ...styles.cardContent, p: { xs: 2, md: 2 } }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									mb: 1,
									flexWrap: { xs: 'wrap', sm: 'nowrap' },
								}}
							>
								<Typography
									variant="subtitle2"
									sx={{
										...styles.title,
										mb: { xs: 1, sm: 0 },
									}}
								>
									Sales & Purchase
								</Typography>

								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<CalendarIcon
										sx={{
											fontSize: '0.9rem',
											mr: 0.5,
											color: styles.subtitle.color,
											transition: styles.subtitle.transition,
										}}
									/>
									<FormControl
										size="small"
										variant="standard"
										sx={{ minWidth: 80 }}
									>
										<Select
											value={timePeriod}
											onChange={handleTimePeriodChange}
											sx={styles.selectStyle}
											IconComponent={ArrowDownIcon}
											disableUnderline
											MenuProps={styles.selectMenuProps}
										>
											<MenuItem value="weekly">Weekly</MenuItem>
											<MenuItem value="monthly">Monthly</MenuItem>
											<MenuItem value="annually">Annually</MenuItem>
										</Select>
									</FormControl>
								</Box>
							</Box>

							<ResponsiveContainer width="100%" height={220}>
								<BarChart
									data={monthlyChartData}
									margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
								>
									<CartesianGrid {...styles.cartesianGrid} />
									<XAxis
										dataKey="month"
										axisLine={false}
										tickLine={false}
										tick={styles.axisTick}
										interval={'preserveStartEnd'}
									/>
									<YAxis
										axisLine={false}
										tickLine={false}
										tick={styles.axisTick}
										width={30}
									/>
									<Tooltip
										contentStyle={styles.tooltipStyle}
										labelStyle={{ color: styles.tooltipStyle.color }}
										itemStyle={{ color: styles.tooltipStyle.color }}
									/>
									<Legend
										wrapperStyle={{ paddingTop: 10 }}
										iconType="circle"
										iconSize={8}
										formatter={(value) => (
											<span style={{ color: styles.title.color }}>{value}</span>
										)}
									/>

									{/* Gradient definitions */}
									<defs>
										<linearGradient
											id="salesGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor={styles.gradients.sales.startColor}
											/>
											<stop
												offset="50%"
												stopColor={styles.gradients.sales.midColor}
											/>
											<stop
												offset="100%"
												stopColor={styles.gradients.sales.endColor}
											/>
										</linearGradient>
										<linearGradient
											id="purchaseGradient"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="0%"
												stopColor={styles.gradients.purchase.startColor}
											/>
											<stop
												offset="50%"
												stopColor={styles.gradients.purchase.midColor}
											/>
											<stop
												offset="100%"
												stopColor={styles.gradients.purchase.endColor}
											/>
										</linearGradient>
									</defs>

									<Bar
										dataKey="revenue"
										name="Revenue"
										fill="url(#salesGradient)"
										radius={[4, 4, 0, 0]}
										barSize={12}
									/>
									<Bar
										dataKey="cost"
										name="Cost"
										fill="url(#purchaseGradient)"
										radius={[4, 4, 0, 0]}
										barSize={12}
									/>
								</BarChart>
							</ResponsiveContainer>
						</CardContent>
					</Card>

					{/* Top Selling Stock Card */}
					<Card
						sx={{
							...styles.card,
							flex: { xs: '0 0 auto', md: '1 0 25%' },
							width: '100%',
							height: { xs: '300px', md: '100%' },
							display: 'flex',
							flexDirection: 'column',
							mb: { xs: 1, md: 0 },
						}}
					>
						<CardContent
							sx={{
								...styles.cardContent,
								p: { xs: 1.5, md: 2 }, // Slightly reduced padding
								pb: { xs: 1.5, md: 2 },
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								'&:last-child': { pb: 2 },
							}}
						>
							<Typography variant="subtitle2" sx={styles.topSellingStockTitle}>
								Top Selling Stock
							</Typography>

							{/* Table header */}
							<Box sx={styles.topSellingStockHeader}>
								<Box sx={{ flex: 3 }}>Product</Box>
								<Box sx={{ flex: 2, textAlign: 'center' }}>Sold Qty</Box>
								<Box sx={{ flex: 2, textAlign: 'center' }}>Remaining</Box>
								<Box sx={{ flex: 2, textAlign: 'right' }}>Price</Box>
							</Box>

							{/* Products data container */}
							<Box
								sx={{
									flex: 1,
									overflowY: 'auto',
									'&::-webkit-scrollbar': {
										width: '6px',
									},
									'&::-webkit-scrollbar-track': {
										background:
											theme.palette.mode === 'dark'
												? 'rgba(255, 255, 255, 0.05)'
												: '#f1f1f1',
										borderRadius: '10px',
									},
									'&::-webkit-scrollbar-thumb': {
										background:
											theme.palette.mode === 'dark'
												? 'rgba(255, 255, 255, 0.2)'
												: '#c1c1c1',
										borderRadius: '10px',
										'&:hover': {
											background:
												theme.palette.mode === 'dark'
													? 'rgba(255, 255, 255, 0.3)'
													: '#a1a1a1',
										},
									},
									scrollbarWidth: 'thin',
									scrollbarColor:
										theme.palette.mode === 'dark'
											? 'rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05)'
											: '#c1c1c1 #f1f1f1',
								}}
							>
								{loading ? (
									<Typography
										align="center"
										sx={{
											py: 2,
											fontSize: '0.75rem',
											color: theme.palette.text.secondary,
										}}
									>
										Loading products...
									</Typography>
								) : error ? (
									<Typography
										align="center"
										color="error"
										sx={{ py: 2, fontSize: '0.75rem' }}
									>
										{error}
									</Typography>
								) : topProducts && topProducts.length === 0 ? (
									<Typography
										align="center"
										sx={{
											py: 2,
											fontSize: '0.75rem',
											color: theme.palette.text.secondary,
										}}
									>
										No product data available
									</Typography>
								) : (
									topProducts &&
									topProducts.map((product, index) => (
										<Box
											key={index}
											sx={{
												...styles.topSellingStockItem,
												borderBottom:
													index < topProducts.length - 1
														? `1px solid ${
																theme.palette.mode === 'dark'
																	? 'rgba(255, 255, 255, 0.1)'
																	: '#F1F5F9'
															}`
														: 'none',
											}}
										>
											<Box sx={styles.topSellingStockProductName}>
												{product.name}
											</Box>
											<Box
												sx={{
													flex: 2,
													textAlign: 'center',
													...styles.topSellingStockData,
												}}
											>
												{product.sold}
											</Box>
											<Box
												sx={{
													flex: 2,
													textAlign: 'center',
													...styles.topSellingStockData,
												}}
											>
												{product.stock} units
											</Box>
											<Box
												sx={{
													flex: 2,
													textAlign: 'right',
													...styles.topSellingStockData,
												}}
											>
												KES {product.price}
											</Box>
										</Box>
									))
								)}
							</Box>

							{/* Visual indicator for scrollable content */}
							{topProducts && topProducts.length > 5 && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										mt: 0.5,
										color:
											theme.palette.mode === 'dark'
												? 'rgba(255,255,255,0.3)'
												: 'rgba(0,0,0,0.2)',
									}}
								>
									<KeyboardArrowDownIcon fontSize="small" />
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Second Column */}
				<Grid
					item
					xs={12}
					md={5}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: { xs: 'auto', md: '100%' },
						mb: { xs: 6, md: 0 },
						position: 'relative',
						zIndex: 1,
					}}
				>
					{/* Inventory Summary Card */}
					<Card
						sx={{
							...styles.card,
							mb: { xs: 1, md: 1 },
							flex: { xs: '0 0 auto', md: '0 0 20%' },
							minHeight: { xs: '160px', md: 'auto' },
							width: '100%',
						}}
					>
						<CardContent sx={{ ...styles.cardContent, p: { xs: 2, md: 2 } }}>
							<Typography
								variant="subtitle2"
								sx={{
									...styles.title,
									mb: 2,
								}}
							>
								Inventory Summary
							</Typography>

							<Box
								sx={{
									display: 'flex',
									flexDirection: { xs: 'row', sm: 'row' },
									height: { xs: 'auto', md: '80px' },
								}}
							>
								{/* Quantity at Hand */}
								<Box
									sx={{
										flex: 1,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
										alignItems: 'center',
										borderRight: `1px solid`,
										borderColor: styles.cardDivider.borderColor,
										pr: 2,
										py: { xs: 1, md: 0 },
										transition: styles.cardDivider.transition,
									}}
								>
									<img
										src={quantityIcon}
										alt="Quantity"
										style={styles.summaryIcon}
									/>

									<Typography variant="h5" sx={styles.quantityValue}>
										{loading ? '...' : getTotalQuantity()}
									</Typography>

									<Typography variant="body2" sx={styles.summaryLabel}>
										Quantity at Hand
									</Typography>
								</Box>

								{/* Number of Categories */}
								<Box
									sx={{
										flex: 1,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
										alignItems: 'center',
										pl: 2,
										py: { xs: 1, md: 0 },
									}}
								>
									<img
										src={categoriesIcon}
										alt="Sales"
										style={styles.summaryIcon}
									/>

									<Typography variant="h5" sx={styles.categoriesValue}>
										{loading ? '...' : getUniqueCategories()}
									</Typography>
									<Typography variant="body2" sx={styles.summaryLabel}>
										Number of Categories
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>

					{/* Order Summary Chart Card */}
					<Card
						sx={{
							...styles.card,
							mb: { xs: 1, md: 1 },
							flex: { xs: '0 0 auto', md: '1 0 40%' },
							minHeight: { xs: '280px', md: 'auto' },
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<CardContent
							sx={{
								...styles.cardContent,
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
								p: { xs: 2, md: 2 },
								pt: { xs: 2.5, md: 2.5 },
								pb: { xs: 2, md: 2 },
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									mb: 2,
									flexWrap: { xs: 'wrap', sm: 'nowrap' },
								}}
							>
								<Typography
									variant="subtitle2"
									fontWeight={600}
									sx={styles.title}
								>
									Order Summary
								</Typography>

								{/* Custom legend with matching colors */}
								<Box sx={{ display: 'flex', gap: 2 }}>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Box
											sx={{
												...styles.legendDot,
												bgcolor: styles.chartColors.orders.main,
											}}
										/>
										<Typography
											variant="caption"
											sx={{
												...styles.legendText,
												color: styles.chartColors.orders.main,
											}}
										>
											Orders
										</Typography>
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Box
											sx={{
												...styles.legendDot,
												bgcolor: styles.chartColors.delivered.main,
											}}
										/>
										<Typography
											variant="caption"
											sx={{
												...styles.legendText,
												color: styles.chartColors.delivered.main,
											}}
										>
											Delivered
										</Typography>
									</Box>
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									width: '100%',
									flex: 1,
								}}
							>
								<ResponsiveContainer width="100%" height={180}>
									<ComposedChart
										data={getMonthlyOrderStats(orders, 12)}
										margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
									>
										{/* Gradient definitions */}
										<defs>
											{/* Orders gradient */}
											<linearGradient
												id={styles.chartColors.orders.gradient.id}
												x1="0"
												y1="0"
												x2="0"
												y2="1"
											>
												{styles.chartColors.orders.gradient.stops.map(
													(stop, index) => (
														<stop
															key={`orders-${index}`}
															offset={stop.offset}
															stopColor={stop.color}
															stopOpacity={stop.opacity}
														/>
													),
												)}
											</linearGradient>

											{/* Delivered gradient */}
											<linearGradient
												id={styles.chartColors.delivered.gradient.id}
												x1="0"
												y1="0"
												x2="0"
												y2="1"
											>
												{styles.chartColors.delivered.gradient.stops.map(
													(stop, index) => (
														<stop
															key={`delivered-${index}`}
															offset={stop.offset}
															stopColor={stop.color}
															stopOpacity={stop.opacity}
														/>
													),
												)}
											</linearGradient>
										</defs>

										<CartesianGrid {...styles.cartesianGrid} />
										<XAxis
											dataKey="month"
											axisLine={false}
											tickLine={false}
											tick={styles.axisTick}
											dy={5}
										/>
										<YAxis
											axisLine={false}
											tickLine={false}
											width={25}
											tick={styles.axisTick}
											dx={-5}
										/>

										<Tooltip
											contentStyle={styles.tooltipStyle}
											labelStyle={{
												fontWeight: 600,
												marginBottom: 4,
												color: styles.tooltipStyle.color,
											}}
											itemStyle={{ color: styles.tooltipStyle.color }}
											cursor={styles.chartCursor}
										/>

										<Area
											type="natural"
											dataKey="orders"
											fill={`url(#${styles.chartColors.orders.gradient.id})`}
											stroke="none"
										/>

										<Area
											type="natural"
											dataKey="delivered"
											fill={`url(#${styles.chartColors.delivered.gradient.id})`}
											stroke="none"
										/>

										<Line
											type="natural"
											dataKey="orders"
											stroke={styles.chartColors.orders.main}
											strokeWidth={styles.chartLine.strokeWidth}
											dot={{
												...styles.chartLine.dot,
												fill: styles.chartColors.orders.main,
											}}
											activeDot={styles.chartLine.activeDot}
											name="Orders"
										/>

										<Line
											type="natural"
											dataKey="delivered"
											stroke={styles.chartColors.delivered.main}
											strokeWidth={styles.chartLine.strokeWidth}
											dot={{
												...styles.chartLine.dot,
												fill: styles.chartColors.delivered.main,
											}}
											activeDot={styles.chartLine.activeDot}
											name="Delivered"
										/>
									</ComposedChart>
								</ResponsiveContainer>
							</Box>
						</CardContent>
					</Card>

					{/* Low Quantity Stock Card */}
					<Card
						sx={{
							...styles.card,
							flex: { xs: '0 0 auto', md: '1 0 40%' },
							width: '100%',
							height: { xs: '300px', md: '100%' },
							display: 'flex',
							flexDirection: 'column',
							mb: { xs: 2, md: 0 },
						}}
					>
						<CardContent
							sx={{
								...styles.cardContent,
								p: { xs: 2, md: 2 },
								pb: { xs: 2, md: 2 },
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								'&:last-child': { pb: 2 },
							}}
						>
							<Typography
								variant="subtitle2"
								sx={{
									...styles.title,
									mb: 2,
								}}
							>
								Low Quantity Stock
							</Typography>

							<Box
								sx={{
									...styles.scrollableContent,
									height: { xs: '220px', md: '160px' },
								}}
							>
								{loading ? (
									<Box sx={styles.emptyStateContainer}>
										<Typography sx={styles.title}>Loading...</Typography>
									</Box>
								) : getLowStockItems().length === 0 ? (
									<Box sx={styles.emptyStateContainer}>
										<Typography sx={styles.subtitle}>
											No low stock items
										</Typography>
									</Box>
								) : (
									getLowStockItems().map((drug, index) => (
										<Box key={drug.id || index} sx={styles.itemContainer}>
											<Box sx={styles.itemIconContainer}>
												{drug.img ? (
													<Box
														component="img"
														src={drug.img}
														alt={drug.name}
														sx={styles.itemImage}
													/>
												) : (
													<MedicationIcon sx={styles.itemIcon} />
												)}
											</Box>
											<Box sx={{ flexGrow: 1 }}>
												<Typography variant="body2" sx={styles.itemName}>
													{drug.name}
												</Typography>
												<Typography variant="caption" sx={styles.itemSubtitle}>
													{drug.stock} units remaining
												</Typography>
											</Box>
											<Box
												sx={{
													...styles.statusIndicator,
													...(drug.stock === 0
														? styles.outOfStockIndicator
														: styles.lowStockIndicator),
												}}
											>
												{drug.stock === 0 ? (
													<>
														<CloseIcon sx={styles.statusIcon} />
														OUT OF STOCK
													</>
												) : (
													<>
														<WarningIcon sx={styles.statusIcon} />
														LOW STOCK
													</>
												)}
											</Box>
										</Box>
									))
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

export default Dashboard;
